/**
 * MCP tool catalog — the single source of truth for the tools the infras MCP
 * server exposes to AI agents AND for the `/mcp` docs page. The MCP server
 * registers these (execution handlers live in apps/mcp); the web renders them.
 *
 * The inventory tools are READ-ONLY — secret tools return NAMES ONLY (values are
 * never stored, so they cannot be read). Writes are flagged `writes: true` and come
 * in two shapes:
 *   · access requests — an agent may FILE, COMMENT and withdraw its OWN request, but
 *     the approve/deny DECISION stays a human UI action (there is no `decide` tool).
 *   · curated data — `update_repo` (product line / service / Owner / Maintainer) and
 *     `set_key_note` (a config key's description). Both are root|maintainer, gated in
 *     rbac.ts `MAINTAINER_MCP_TOOLS`, and hidden from a lower role's tools/list.
 *
 * Curated writes use ONE `update_<resource>` tool per resource, not one tool per
 * field — tools/list is context paid on every request, and two tools with
 * overlapping descriptions make the model pick wrong. Scanner-owned columns are
 * never settable: the scan rewrites them each pass, so the edit would revert.
 */

import {
  ACCESS_DURATION_DAYS,
  ACCESS_REQUEST_RESOURCE_NAME_MAX,
  ACCESS_RESOURCE_TYPES,
} from './access-requests';

export interface McpToolParam {
  type: 'string' | 'number' | 'boolean' | 'array';
  description: string;
  /** Element schema — REQUIRED when type is 'array'. Without it the model gets an
   *  untyped list and guesses at the element shape. */
  items?: { type: 'string' | 'number' };
  /** Optional JSON Schema maxLength (string params). */
  maxLength?: number;
  /**
   * Optional JSON Schema enum — the closed set of accepted values. Worth setting
   * whenever the server validates against a list: the model then picks a legal
   * value instead of inventing one and learning the constraint from an error.
   */
  enum?: readonly (string | number)[];
}

export interface McpTool {
  name: string;
  title: string;
  description: string;
  category: 'search' | 'repos' | 'clusters' | 'secrets' | 'scheduler' | 'catalog' | 'access' | 'errors';
  /** True for tools that MUTATE (access-request create/comment). Omitted = read-only. */
  writes?: boolean;
  inputSchema: {
    type: 'object';
    properties: Record<string, McpToolParam>;
    required?: string[];
    additionalProperties?: boolean;
  };
  /** A representative call, for the docs page. */
  example: string;
}

const obj = (
  properties: Record<string, McpToolParam>,
  required: string[] = [],
): McpTool['inputSchema'] => ({ type: 'object', properties, required, additionalProperties: false });

export const MCP_TOOLS: McpTool[] = [
  {
    name: 'search',
    title: 'Global search',
    description:
      'Search the whole infra inventory (repos, clusters, namespaces, services, and secret/key names) by keyword. Workloads are deliberately not indexed (they duplicated their repo) — use get_repo or service_graph for "where does it run". Substring + full-text + typo-tolerant trigram. Each hit carries a `tier` saying WHERE it matched — 5 exact title, 4 title, 3 body (a secret bundle\'s env-var key names), 2 subtitle, 1 token-only, 0 fuzzy near-miss — and results are ordered by tier, then by most-recent GitLab activity within a tier, live repos before archived. A tier-0 hit means the term appears nowhere in the row, so treat it as a suggestion, not an answer. If `withheld` is above zero, secret matches exist that your access level hides: the names are withheld, not their existence, so ask for access rather than reporting that they do not exist. Use this first when you only have a name/fragment (e.g. an app name, a config key like MONGO_SMALLAPP_URL, a cluster).',
    category: 'search',
    inputSchema: obj(
      {
        query: { type: 'string', description: 'Keyword or fragment (>=2 chars).' },
        limitPerType: { type: 'number', description: 'Max hits per entity type (default 5, max 25).' },
        entityType: {
          type: 'string',
          description:
            'Narrow to one doc type: repo | secret | cluster | namespace | service | catalog | errorcode. Omit for all types. An unknown value returns nothing rather than everything.',
        },
      },
      ['query'],
    ),
    example: 'search(query: "canva")  ·  search(query: "MONGO_SCIO", entityType: "secret")',
  },
  {
    name: 'list_repos',
    title: 'List repos',
    description:
      'List GitLab repos in the inventory, with curation facets. Filter by product line (from list_product_lines), group path, kind, owner-status, and archived-status. ARCHIVED repos are EXCLUDED by default (archived: "active") — they are not the daily curation surface; pass archived: "all"/"archived" to include/isolate them. Returns path, gitlabProjectId (the id update_repo takes), group, visibility, default branch, product line, kind, the assigned owner + maintainer emails, and how many k8s deployments run the repo image.',
    category: 'repos',
    inputSchema: obj({
      productLine: { type: 'string', description: 'Filter by product line (exact; e.g. smallapp, scio-core, devices — see list_product_lines).' },
      group: { type: 'string', description: 'Filter by group_path (substring).' },
      classification: {
        type: 'string',
        description: 'Filter by kind: service | api | job | frontend | lib, or "unknown" (no deployed service).',
      },
      needsOwner: { type: 'boolean', description: 'Only repos with NO assigned owner (the curation backlog).' },
      archived: { type: 'string', description: 'active (default — exclude archived) | archived (only archived) | all.' },
      limit: { type: 'number', description: 'Max rows (default 100).' },
    }),
    example: 'list_repos(productLine: "smallapp", needsOwner: true)',
  },
  {
    name: 'get_repo',
    title: 'Get repo detail',
    description:
      "The one-shot 'what / where / deploy / env' answer for a repo (by path or last segment). WHAT: product line + confidence + kind + the service/catalog it belongs to, plus its assigned owner (accountable) + maintainer (incident contact). WHERE + DEPLOY: every k8s deployment of its image — cluster, namespace, image tag, replicas. ENV: the env-var NAMES on each Deployment spec, plus inheritedConfig (var names from its shared @optisigns packages). Names only — never values.",
    category: 'repos',
    inputSchema: obj(
      { name: { type: 'string', description: 'Repo path or last path segment (e.g. optisigns-smallapp-rest-canva).' } },
      ['name'],
    ),
    example: 'get_repo(name: "optisigns-open-api")',
  },
  {
    name: 'list_clusters',
    title: 'List clusters',
    description:
      'List all Kubernetes clusters: name, cloud/DO account, region, k8s version, LB IP, node count, reachability, and the curated description of what runs there.',
    category: 'clusters',
    inputSchema: obj({}),
    example: 'list_clusters()',
  },
  {
    name: 'get_cluster',
    title: 'Get cluster detail',
    description:
      'Full detail for one cluster by name: topology (region, version, nodes, LB IP), the curated description, and its namespaces.',
    category: 'clusters',
    inputSchema: obj({ name: { type: 'string', description: 'Cluster name (e.g. k8s-optisigns-prod-smallapp-rest).' } }, ['name']),
    example: 'get_cluster(name: "k8s-optisigns-prod-smallapp-rest")',
  },
  {
    name: 'list_secrets',
    title: 'List secrets',
    description:
      'List secrets (NAMES ONLY — no values). Filter by env (dev/sandbox/qa/beta/prod) and/or provider (gcp/aws). Shows whether each is a JSON bundle and how many keys it holds.',
    category: 'secrets',
    inputSchema: obj({
      env: { type: 'string', description: 'dev | sandbox | qa | beta | prod (matches derived env).' },
      provider: { type: 'string', description: 'gcp | aws.' },
      limit: { type: 'number', description: 'Max rows (default 100).' },
    }),
    example: 'list_secrets(env: "qa")',
  },
  {
    name: 'get_secret',
    title: 'Get secret detail',
    description:
      'Full detail for one logical secret by (canonical) name — its per-environment instances (project, env, created), the union of its key NAMES with which envs each key appears in, PLUS each key\'s editorial description (markdown context: what the key is, who owns it, which app uses it, where to set non-prod/prod values). Values are never returned; descriptions are names+context only. Set a key description with set_key_note.',
    category: 'secrets',
    inputSchema: obj({ name: { type: 'string', description: 'Secret canonical name (bundles collapse to <path>--keys).' } }, ['name']),
    example: 'get_secret(name: "optisigns-open-api--keys")',
  },
  {
    name: 'find_secret_key',
    title: 'Find a config key',
    description:
      'Answer "where does config key X live?" — given a key NAME (e.g. STRIPE_KEY, MONGO_SMALLAPP_URL), return every secret/bundle that contains it (provider, project, env) plus its editorial description (markdown context) if one is set. Values are never returned.',
    category: 'secrets',
    inputSchema: obj({ keyName: { type: 'string', description: 'Exact config key name to locate.' } }, ['keyName']),
    example: 'find_secret_key(keyName: "MONGO_SMALLAPP_URL")',
  },
  {
    name: 'set_key_note',
    title: 'Set a config-key description',
    description:
      'Set (or clear) the editorial DESCRIPTION of a config KEY — the markdown context surfaced to humans (secret-detail page) and AI (get_secret / find_secret_key). Write what the key is, who owns it, which app consumes it, and where to set its non-prod vs prod value. GLOBAL by key name (a key\'s meaning is intrinsic, so one description covers every bundle/env it appears in). Markdown is supported. Empty note clears it. Requires a root or maintainer PAT. Never include a secret VALUE — this is names + context only.',
    category: 'secrets',
    writes: true,
    inputSchema: obj(
      {
        keyName: { type: 'string', description: 'Exact config key name to describe (e.g. APP_SECRET_KEY).' },
        note: { type: 'string', description: 'Markdown description (context, NOT the value). Empty string clears it. Max 20000 chars.' },
      },
      ['keyName', 'note'],
    ),
    example: 'set_key_note(keyName: "APP_SECRET_KEY", note: "## APP_SECRET_KEY\\nSmallApp HMAC signing secret. Owner: Platform. Used by open-api + smallapp REST. Prod: shared--keys; non-prod: <env> keyset.")',
  },
  {
    name: 'list_jobs',
    title: 'List scheduler jobs',
    description:
      'List the scheduler jobs (scanners that keep the inventory fresh): name, what it does, cron schedule, enabled state, and the last run — status, time, duration and error. Use get_job for run history and timing percentiles.',
    category: 'scheduler',
    inputSchema: obj({}),
    example: 'list_jobs()',
  },
  {
    name: 'get_job',
    title: 'Get scheduler job detail',
    description:
      'One job: its definition (what it does, cron, enabled, singleton, timeout, params) plus recent run history and a timing summary — runs, success rate, p50/p95/max duration, and the interval actually observed between runs. This is the tool for "is this job healthy", "what does a run cost", and "is its cron too slow/too aggressive" — answer those from here rather than from cluster access. Per-run log lines are not included; the dashboard job page has the log viewer.',
    category: 'scheduler',
    inputSchema: obj(
      {
        name: { type: 'string', description: 'Job name, as returned by list_jobs (e.g. gitlab-scan).' },
        limit: { type: 'number', description: 'How many recent runs to return (default 20, max 200). The timing summary covers exactly these runs.' },
      },
      ['name'],
    ),
    example: 'get_job(name: "gitlab-scan", limit: 50)',
  },
  {
    name: 'list_catalog',
    title: 'List deploy catalog',
    description:
      'List deployment-catalog entries — "how we deploy a class of application" (vs the inventories, which record what exists). Call this BEFORE deploying or bootstrapping an app to find the recipe entry (e.g. smallapp-v3), then get_catalog for the full recipe.',
    category: 'catalog',
    inputSchema: obj({}),
    example: 'list_catalog()',
  },
  {
    name: 'get_catalog',
    title: 'Get deploy recipe',
    description:
      'Full deploy recipe for one catalog entry by name: layers (rest/jobs/screen), build (images, registry, tags), secret bundle convention + deploy scripts, k8s placement, runbook, known issues, AND a per-environment DEPLOYMENT MATRIX (spec.environments) — one fully-specified row per env with cluster + DO cluster id, namespace, kubeconfig secret, backend URL, ingress name/LB-IP/TLS secret, CDN URL + S3 bucket + CloudFront id, GCP secrets project, per-app K8s Secret, and the --keys bundle. Read spec.environments to locate or re-verify a deployment; pick the row for your target env.',
    category: 'catalog',
    inputSchema: obj(
      { name: { type: 'string', description: 'Catalog entry name (from list_catalog), e.g. smallapp-v3.' } },
      ['name'],
    ),
    example: 'get_catalog(name: "smallapp-v3")',
  },
  {
    name: 'list_error_codes',
    title: 'List error codes',
    description:
      'The customer error dictionary: every code a customer can be shown, per lane. A code is layer 2 of the emitter contract — the STABLE, customer-facing half. Layer 1 is `reason`, an internal routing key that alert rules filter on and that may be renamed at any time; never quote a reason to a customer. Returns code, reason, lane, level, taxonomy class and the customer copy.',
    category: 'errors',
    inputSchema: obj({
      lane: { type: 'string', description: 'Narrow to one lane key, e.g. "mdm". Omit for all lanes.' },
    }),
    example: 'list_error_codes()  ·  list_error_codes(lane: "mdm")',
  },
  {
    name: 'list_log_lanes',
    title: 'List emitter lanes',
    description:
      'Every structured-logging lane in the estate: its `evt` (the token every Loki rule prefilters on, e.g. |= "mdm_fail"), owning repo, contract module, and whether it is a backend lane (queried | regexp) or a browser/Faro lane (queried | logfmt). The unit is the SURFACE, not the repo — open-api owns two lanes and scio3 owns three. ⚠️ Naming is inconsistent (six *_fail, three *_error), so a query that assumes _fail silently matches nothing against the Faro lanes. Use this to find which lane a log line belongs to, or which module owns a taxonomy.',
    category: 'errors',
    inputSchema: obj({}),
    example: 'list_log_lanes()',
  },
  {
    name: 'get_error_code',
    title: 'Explain an error code',
    description:
      'Resolve ONE code (or the internal `reason` behind it) to the exact copy a customer should be given, plus the renderer rules for it. USE THE `customerMessage` VERBATIM — it is reviewed copy, and several codes are deliberately generic because the honest cause is our own infrastructure or is not attributable to one account. Do not improve it, do not add the cause, and never surface the `reason`, the `note`, or any raw log field to a customer. Read `note` before rendering: some carry hard rules, e.g. `unsupported_request` must not be shown unless the event belongs to the asking account.',
    category: 'errors',
    inputSchema: obj(
      { code: { type: 'string', description: 'The customer code (e.g. device_clock_out_of_sync) OR the internal reason (e.g. auth_signature_expired).' } },
      ['code'],
    ),
    example: 'get_error_code(code: "device_clock_out_of_sync")',
  },
  {
    name: 'list_services',
    title: 'List services',
    description:
      'List classified services (apps), optionally filtered by PRODUCT LINE. Returns name, product line, kind, classification confidence + source, member repo count, and how many clusters it runs in. This is the entry point for the product-line model — answers "what apps are in the <product line> line?" — then service_graph for one service\'s placement.',
    category: 'repos',
    inputSchema: obj({
      productLine: { type: 'string', description: 'Filter to one product line (e.g. scio-core | ads-portal | optisound | smallapp).' },
      limit: { type: 'number', description: 'Max rows (default 100).' },
    }),
    example: 'list_services(productLine: "smallapp")',
  },
  {
    name: 'service_graph',
    title: 'Which app runs where',
    description:
      'The service→workload→cluster spine ("which app runs where"). Returns graph nodes (service / workload / cluster) + edges. Filter by `service` (where does this service run) OR `cluster` (what runs in this cluster). Edge confidence: deployedTo (service→workload) is "confirmed" (exact image==repo path), "monorepo" (a deterministic sub-path image of a multi-image repo), or "inferred" (genuinely fuzzy — none today); runsOn (workload→cluster) is always "confirmed". Only classified services appear — use list_repos(classification:"unknown") to find gaps and update_repo to fill them.',
    category: 'repos',
    inputSchema: obj({
      service: { type: 'string', description: 'Focus one service by name (from list_repos / services).' },
      cluster: { type: 'string', description: 'Focus one cluster by name (what services run in it).' },
    }),
    example: 'service_graph(service: "ads-portal-open-api")',
  },
  {
    name: 'list_assignable_users',
    title: 'People who can be assigned as repo Owner/Maintainer',
    description:
      "The infras-app users a repo's owner or maintainer can be set to (anyone who has signed in or holds a role). Call this BEFORE update_repo to get the exact email — update_repo rejects an email that is not on this list, so a typo fails loudly instead of creating a phantom owner.",
    category: 'repos',
    inputSchema: obj({}),
    example: 'list_assignable_users()',
  },
  {
    name: 'update_repo',
    title: "Update a repo's curated fields",
    description:
      "Update any of a repo's CURATED fields in one call. Omitted field = unchanged; pass an EMPTY STRING to clear `owner`, or an EMPTY LIST to clear `maintainers`/`engineers`. All supplied fields apply in ONE transaction — a rejected value means nothing is written, never a half-update.\n\nCLASSIFICATION: `productLine` is the primary bucket — use an EXISTING line from list_product_lines (don't invent one; that causes taxonomy sprawl). product_line and kind both auto-derive from the repo path, so pass `productLine` only to OVERRIDE a wrong heuristic — that override is authoritative and survives every subsequent scan/stitch. `classification` is the kind (service | api | job | frontend | lib). One repo ↔ one service, so `serviceName` MOVES the repo. Call list_needs_review for the low-confidence backlog.\n\nOWNERSHIP: `owner` = accountable stakeholder/decision-maker (exactly ONE person — re-assigning REPLACES the incumbent). `maintainers` = incident contacts and `engineers` = who to pull in to work on it; both are ROSTERS taking a list, and the list you pass becomes the ENTIRE membership — so pass the full set you want, not a delta, and a repeated identical call is idempotent. Read the current roster from get_repo/list_repos first if you mean to add to it rather than replace it. Every email must come from list_assignable_users (assigning someone who has never signed in is possible, but only from the dashboard).\n\nNOT settable: path, group, visibility, defaultBranch, archived, lastActivityAt — the GitLab scanner owns those and rewrites them every pass, so an edit here would silently revert. Root/maintainer only; human-assigned values survive rescans. Read the result back with get_repo or list_repos.",
    category: 'repos',
    writes: true,
    inputSchema: obj(
      {
        gitlabProjectId: { type: 'number', description: "The repo's GitLab project id (from get_repo or list_repos)." },
        productLine: { type: 'string', description: 'Product line (primary bucket). Use an existing value from list_product_lines; overrides the path heuristic when wrong.' },
        classification: { type: 'string', description: 'Kind: service | api | job | frontend | lib. Auto-derived from the path if omitted.' },
        serviceName: { type: 'string', description: "Service to group the repo under. Defaults to the repo's short name. Changing it MOVES the repo." },
        catalogName: { type: 'string', description: 'Deploy-catalog name (e.g. smallapp-v3), if applicable.' },
        description: { type: 'string', description: 'Human note about the service.' },
        owner: { type: 'string', description: "Accountable stakeholder, by email (from list_assignable_users). Empty string clears the role." },
        maintainers: {
          type: 'array',
          items: { type: 'string' },
          description:
            "Incident contacts, by email (from list_assignable_users). The list REPLACES the whole roster — pass every maintainer you want, not just the new one. Empty list clears the role.",
        },
        engineers: {
          type: 'array',
          items: { type: 'string' },
          description:
            "Engineers to pull in for work on this repo, by email (from list_assignable_users). The list REPLACES the whole roster. Empty list clears the role.",
        },
      },
      ['gitlabProjectId'],
    ),
    example: 'update_repo(gitlabProjectId: 12345, productLine: "smallapp", owner: "someone@optisigns.com")',
  },
  {
    name: 'list_needs_review',
    title: 'Classification review queue',
    description:
      'REPOS needing a product line — either unclassified, or a low-confidence (< 70) non-human derivation. The dashboard "Needs classification" backlog. Returns repo path, gitlabProjectId, current product line, confidence, source. Archived repos are excluded. Resolve each with update_repo (pass a productLine from list_product_lines). Human/root confirmations are authoritative and never appear here.',
    category: 'repos',
    inputSchema: obj({ limit: { type: 'number', description: 'Max rows (default 100).' } }),
    example: 'list_needs_review()',
  },
  {
    name: 'list_product_lines',
    title: 'List product lines',
    description:
      'The product-line taxonomy — the valid buckets update_repo accepts for `productLine`, with how many repos are in each (total + active). Call this BEFORE curating so you assign an EXISTING line rather than inventing a near-duplicate (which fragments the taxonomy).',
    category: 'repos',
    inputSchema: obj({}),
    example: 'list_product_lines()',
  },
  {
    name: 'bulk_assign_owner',
    title: 'Bulk-assign owner/maintainer by filter',
    description:
      'Assign ONE person as owner or maintainer across every repo matching a filter — the "all smallapps are managed by Alice" workflow, in one call instead of updating each repo. Supply a filter (productLine and/or group; a naked call with no filter is refused) + role + email (from list_assignable_users). BEST-EFFORT: each repo\'s authorization is re-checked and a per-repo failure lands in `skipped`, not aborting the rest. Re-assigning REPLACES the current holder (idempotent). Archived repos are never touched. Every success is audited with the prior owner + the filter scope. root|maintainer only.',
    category: 'repos',
    writes: true,
    inputSchema: obj(
      {
        role: { type: 'string', description: "'owner' (accountable stakeholder) or 'maintainer' (incident contact)." },
        email: { type: 'string', description: 'The person to assign, by email (must be on list_assignable_users).' },
        productLine: { type: 'string', description: 'Filter: only repos in this product line (from list_product_lines).' },
        group: { type: 'string', description: 'Filter: only repos whose group_path contains this substring.' },
        onlyUnowned: { type: 'boolean', description: 'Only assign repos that currently have NO owner (fill the gaps, don\'t reassign).' },
      },
      ['role', 'email'],
    ),
    example: 'bulk_assign_owner(role: "owner", email: "alice@optisigns.com", productLine: "smallapp", onlyUnowned: true)',
  },
  {
    name: 'create_access_request',
    title: 'File an access request',
    description:
      'File a request for access to a company resource, on behalf of the signed-in user (the PAT owner is the requester). Use this instead of pinging people on Slack. Layer-1 reviewers are auto-assigned and pinged; the request is tracked with a full timeline and gets a clear approved/denied answer in the dashboard. You do NOT need a role to file. Note: the approve/deny decision is a human UI action — this tool cannot approve. If the resource type has required questions, filing will fail with what is missing; file from the dashboard for those.',
    category: 'access',
    writes: true,
    inputSchema: obj(
      {
        resourceType: {
          type: 'string',
          enum: ACCESS_RESOURCE_TYPES,
          description: `Which system. One of: ${ACCESS_RESOURCE_TYPES.join(' | ')}.`,
        },
        resourceName: {
          type: 'string',
          description: `Optional precise target (DB name, Grafana host, GCP project id…) — defaults to the system's own label when omitted. Max ${ACCESS_REQUEST_RESOURCE_NAME_MAX} characters.`,
          maxLength: ACCESS_REQUEST_RESOURCE_NAME_MAX,
        },
        maintainerEmail: { type: 'string', description: 'Optional — email of the person who owns the resource (they may be asked to sign off). Omit if unknown.' },
        reason: { type: 'string', description: 'Why you need access. Specific reasons are approved faster. Name the exact target here if you left resourceName blank.' },
        durationKind: {
          type: 'string',
          enum: ['permanent', 'temporary'],
          description:
            "Defaults to 'permanent'. Prefer 'temporary' with a durationDays — a sensitive system granted permanently is filed as HIGH risk and gets a second look.",
        },
        durationDays: {
          type: 'number',
          enum: ACCESS_DURATION_DAYS,
          description: `Required when durationKind is 'temporary', and rejected otherwise. One of: ${ACCESS_DURATION_DAYS.join(', ')}.`,
        },
      },
      ['resourceType', 'reason'],
    ),
    example:
      'create_access_request(resourceType: "database", resourceName: "analytics replica DB", reason: "debug a prod incident", durationKind: "temporary", durationDays: 7)',
  },
  {
    name: 'list_access_requests',
    title: 'List access requests',
    description:
      'List access requests. scope "mine" = requests you filed (any role). scope "review" = the review queue — for root/maintainer this is every request; for anyone else it is only requests they are assigned to or the maintainer of. Optionally filter by status. Visibility is enforced server-side. Use this to triage, then get_access_request for the full detail before recommending.',
    category: 'access',
    inputSchema: obj(
      {
        scope: { type: 'string', description: 'mine (requests you filed) | review (the review queue).' },
        status: { type: 'string', description: 'Optional: pending | approved | denied | cancelled.' },
      },
      ['scope'],
    ),
    example: 'list_access_requests(scope: "review", status: "pending")',
  },
  {
    name: 'get_access_request',
    title: 'Get access request detail',
    description:
      'Full detail for one access request by id: requester, resource, maintainer, status, the submitted answers, and the complete event timeline (assignments, comments, escalations, decision). You may only read a request you filed, are the maintainer of, are assigned to review, or (root/maintainer) any request. Read this to EVALUATE a request before posting a recommendation.',
    category: 'access',
    inputSchema: obj({ id: { type: 'number', description: 'Access request id (from list_access_requests).' } }, ['id']),
    example: 'get_access_request(id: 42)',
  },
  {
    name: 'comment_access_request',
    title: 'Comment on an access request',
    description:
      'Post a comment on an access request — the intended way for an AI agent to record a RECOMMENDATION (e.g. "recommend approve — scoped, time-boxed" or "recommend deny — over-broad, ask for a narrower scope") for the human reviewer. Markdown supported; @mention a teammate to notify them. This does NOT decide the request — a human still clicks approve/deny in the dashboard. Same visibility rule as get_access_request.',
    category: 'access',
    writes: true,
    inputSchema: obj(
      {
        id: { type: 'number', description: 'Access request id.' },
        note: { type: 'string', description: 'Your comment / recommendation. Markdown + @mention supported.' },
      },
      ['id', 'note'],
    ),
    example: 'comment_access_request(id: 42, note: "Recommend **approve** — read-only, time-boxed to the incident.")',
  },
  {
    name: 'cancel_access_request',
    title: 'Cancel an access request you filed',
    description:
      'Withdraw a PENDING access request that YOU filed — use this when the need went away (e.g. the work shipped another way) instead of leaving it in the reviewers\' queue. Owner-only: you cannot cancel a request somebody else filed, whatever your role. The request is kept with status "cancelled" and its full timeline; nothing is deleted, and the requester is emailed. Cancelling is NOT a decision — there is deliberately no approve/deny tool.',
    category: 'access',
    writes: true,
    inputSchema: obj(
      { id: { type: 'number', description: 'Access request id (one you filed, still pending).' } },
      ['id'],
    ),
    example: 'cancel_access_request(id: 37)',
  },
  {
    name: 'edit_access_request',
    title: 'Edit an access request description',
    description:
      'Edit the DESCRIPTION (reason) of an access request you OWN — the free-form "why do you need access" justification. Owner-only (the original requester); it does not change status or notify reviewers. Overwrites the text in place and stamps an edited time. Markdown supported.',
    category: 'access',
    writes: true,
    inputSchema: obj(
      {
        id: { type: 'number', description: 'Access request id (one you filed).' },
        reason: { type: 'string', description: 'The new description / justification. Markdown supported. Replaces the current text.' },
      },
      ['id', 'reason'],
    ),
    example: 'edit_access_request(id: 42, reason: "Updated: read-only replica access, time-boxed to the incident (~2 days).")',
  },
];
